// Pure filtering logic for pluginConfig.modules — kept free of DOM/template
// imports so it can be unit-tested (renderMenu.ts imports menu.html/menu.css,
// which vitest cannot load).

import { PROFILES } from "@/enum/Profiles";

// Maps widget button keys to their corresponding pluginConfig.modules key (camelCase)
export const CONTENT_KEY_MAP: Record<string, string> = {
  'font-size':      'adjustFontSizing',
  'highlight-title': 'highlightTitle',
  'highlight-links': 'highlightLinks',
  'readable-font':  'dyslexicFont',
  'letter-spacing': 'letterSpacing',
  'line-height':    'lineHeight',
  'font-weight':    'fontWeight',
  'text-align':     'textAlign',
};

export const COLOR_KEY_MAP: Record<string, string> = {
  'dark-contrast':  'darkContrast',
  'light-contrast': 'lightContrast',
  'high-contrast':  'highContrast',
  'high-saturation':'highSaturation',
  'low-saturation': 'lowSaturation',
  'monochrome':     'monochrome',
};

export const NAV_KEY_MAP: Record<string, string> = {
  'big-cursor':     'bigCursor',
  'stop-animations':'pauseAnimations',
  'readable-guide': 'readingGuide',
};

// page-read / mute-sounds are single top-level toggles rather than sections;
// each button key doubles as its saved-state key. Shared by renderMenu's
// visibility filter and dropDisabledModuleStates so the mapping lives once.
export const TOP_LEVEL_KEY_MAP: Record<string, 'pageRead' | 'muteSounds'> = {
  'page-read':      'pageRead',
  'mute-sounds':    'muteSounds',
};

export function filterByModule<T extends { key: string }>(
  buttons: readonly T[],
  moduleSection: Record<string, { enabled: boolean }> | undefined,
  keyMap: Record<string, string>
): T[] {
  if (!moduleSection) return buttons as T[];
  return (buttons as T[]).filter((btn) => {
    const configKey = keyMap[btn.key];
    if (!configKey) return true; // unknown key — show by default
    const mod = moduleSection[configKey];
    return mod?.enabled !== false;
  });
}

// The config key each profile button maps to in modules.profiles. Typed
// against PROFILES so the compiler enforces one entry per profile bundle.
export type ProfileConfigKey = 'lowVision' | 'adhd' | 'seizureSafe' | 'epilepsySafe';

export const PROFILE_KEY_MAP: Record<keyof typeof PROFILES, ProfileConfigKey> = {
  'low-vision-profile':    'lowVision',
  'adhd-profile':          'adhd',
  'seizure-safe-profile':  'seizureSafe',
  'epilepsy-safe-profile': 'epilepsySafe',
};

// Object.entries/keys widen keys to string, which would force casts back in —
// a literal list keeps the PROFILES lookups below fully typed. The Record
// type above guarantees this list and PROFILE_KEY_MAP cover the same keys.
const PROFILE_BUTTON_KEYS: ReadonlyArray<keyof typeof PROFILES> = [
  'low-vision-profile',
  'adhd-profile',
  'seizure-safe-profile',
  'epilepsy-safe-profile',
];

// The profiles card — including its "Accessibility Profiles" title/select
// control — disappears when the admin section flag is explicitly off, or when
// every individual profile has been disabled by config.
export function shouldRemoveProfilesSection(
  profilesSection: { enabled?: boolean } | undefined,
  visibleCount: number
): boolean {
  return profilesSection?.enabled === false || visibleCount === 0;
}

// A module disabled in pluginConfig.modules must also reset any effect the
// visitor previously enabled for it (ALLY-141): the menu already hides the
// button, but saved states re-applied on load (runAccessibility/renderTools)
// kept the effect alive on the site. Maps mirror renderTools' state keys —
// every button key doubles as its state key except font-size, which is stored
// as the numeric multiplier `fontSize`; the color modules share the single
// `contrast` state, whose VALUE is the color button key.

interface ModuleToggle {
  enabled?: boolean;
}

// Deliberately a local, loose shape rather than WidgetConfig['modules']: the
// config arrives as remote JSON and may be sparse, and this function only ever
// reads `enabled` — a new module section added to the real type needs an
// explicit key map here anyway before its states could be dropped.
interface ModulesForStateReset {
  color?: Record<string, ModuleToggle>;
  content?: Record<string, ModuleToggle>;
  navigation?: Record<string, ModuleToggle>;
  profiles?: { enabled?: boolean } & Partial<Record<ProfileConfigKey, ModuleToggle>>;
  pageRead?: ModuleToggle;
  muteSounds?: ModuleToggle;
}

const CONTENT_STATE_KEY: Record<string, string> = { 'font-size': 'fontSize' };

export function dropDisabledModuleStates(
  states: Record<string, unknown>,
  modules: ModulesForStateReset | undefined,
): { states: Record<string, unknown>; changed: boolean } {
  if (!modules) return { states, changed: false };
  const next: Record<string, unknown> = { ...states };
  let changed = false;
  const drop = (stateKey: string): void => {
    if (stateKey in next) {
      delete next[stateKey];
      changed = true;
    }
  };
  const isDisabled = (
    section: Record<string, ModuleToggle> | undefined,
    configKey: string,
  ): boolean => section?.[configKey]?.enabled === false;

  for (const [btnKey, configKey] of Object.entries(CONTENT_KEY_MAP)) {
    if (isDisabled(modules.content, configKey)) drop(CONTENT_STATE_KEY[btnKey] ?? btnKey);
  }
  for (const [btnKey, configKey] of Object.entries(NAV_KEY_MAP)) {
    if (isDisabled(modules.navigation, configKey)) drop(btnKey);
  }
  const contrast = next['contrast'];
  if (typeof contrast === 'string') {
    const colorConfigKey = COLOR_KEY_MAP[contrast];
    if (colorConfigKey && isDisabled(modules.color, colorConfigKey)) drop('contrast');
  }
  for (const [btnKey, configKey] of Object.entries(TOP_LEVEL_KEY_MAP)) {
    if (modules[configKey]?.enabled === false) drop(btnKey);
  }

  // A disabled profile unwinds the way disableProfile does: the stored flag
  // goes, and when the visitor had the profile enabled, so does the bundle it
  // applied (its contrast plus every tool state) — the hidden button would
  // otherwise leave the bundle stuck on with no one-click way to undo it.
  // Section-level `enabled: false` disables every profile at once.
  const profilesSection = modules.profiles;
  for (const btnKey of PROFILE_BUTTON_KEYS) {
    const configKey = PROFILE_KEY_MAP[btnKey];
    const profileOff =
      profilesSection?.enabled === false ||
      profilesSection?.[configKey]?.enabled === false;
    if (!profileOff) continue;
    const wasActive = Boolean(next[btnKey]);
    drop(btnKey);
    if (!wasActive) continue;
    const bundle = PROFILES[btnKey];
    drop('contrast');
    bundle.tools.forEach((tool: string) => drop(CONTENT_STATE_KEY[tool] ?? tool));
  }

  return { states: next, changed };
}
